import { isDebug } from './common';

// log-service.ts
interface LogEntry<T = any> {
  id?: number; // Auto-generated ID by IndexedDB
  batch: string;
  timestamp: Date; // Log timestamp
  key: string; // Log key for classification
  data: T; // Arbitrary log payload data
}

interface GetOptions<T = any> {
  key?: string;
  batches?: string[];
  startDate?: Date;
  endDate?: Date;
  filter?: LogFilter<T>;
  keyword?: string;
}

type LogFilter<T = any> = (log: LogEntry<T>) => boolean; // Callback for content-based filtering

class LogService {
  private dbName = 'LogDatabase'; // IndexedDB database name
  private storeName = 'logs'; // Object store name
  private dbVersion = 1; // Database schema version
  private db: IDBDatabase | null = null; // Database instance
  private maxRetentionDays: number; // Maximum days to retain logs
  private lastCleanupTime: number = 0; // Timestamp of last cleanup
  private cleanupInterval: number = 60 * 60 * 1000; // Cleanup check interval (1 hours)
  private batch: string = `${new Date().toLocaleString()}:${new Date().getUTCMilliseconds()}`;

  /**
   * Initialize log service with retention policy
   * @param maxRetentionDays - Maximum days to keep logs (default: 3)
   */
  constructor(maxRetentionDays: number = 3) {
    this.maxRetentionDays = Math.max(1, maxRetentionDays); // Minimum 1 day retention
    this.initializeDB();
    this.setupCleanupSchedule();
  }

  /**
   * Schedule periodic cleanup checks
   */
  private setupCleanupSchedule() {
    // Check every minute if cleanup is needed
    setInterval(() => {
      const now = Date.now();
      // Trigger cleanup if interval has passed
      if (now - this.lastCleanupTime > this.cleanupInterval) {
        this.cleanupOldLogs().catch(console.error);
      }
    }, this.cleanupInterval);
  }

  /**
   * Initialize IndexedDB database
   * @returns Promise that resolves when DB is ready
   */
  private initializeDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        // Perform initial cleanup after DB initialization
        this.cleanupOldLogs().finally(resolve);
      };

      // Handle schema upgrades
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        // Create object store if not exists
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, {
            keyPath: 'id',
            autoIncrement: true,
          });
          // Create indexes for efficient querying
          store.createIndex('timestamp_idx', 'timestamp', { unique: false });
          store.createIndex('category_idx', 'key', { unique: false });
        }
      };
    });
  }

  /**
   * Ensure database is ready before operations
   * @returns Promise resolving to database instance
   */
  private async ready(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    await this.initializeDB();
    return this.db!;
  }

  private sanitizeData(data: any): any {
    if (Array.isArray(data)) {
      return data.map((item) => this.sanitizeData(item));
    }

    if (data && typeof data === 'object') {
      const cleaned: any = {};
      for (const key in data) {
        const value = data[key];
        if (typeof value === 'function') continue;
        cleaned[key] = this.sanitizeData(value);
      }
      return cleaned;
    }

    return data;
  }

  /**
   * Add new log entry
   * @param key - Log key
   * @param data - Log payload data
   * @returns Promise resolving to generated log ID
   */
  public async addLog<T>(key: string, data: T): Promise<number> {
    const db = await this.ready();
    const entry: LogEntry<T> = {
      timestamp: new Date(),
      batch: this.batch,
      key,
      data: this.sanitizeData(data),
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      try {
        const request = store.add(entry);

        request.onsuccess = async () => {
          // Trigger cleanup if interval has passed
          const now = Date.now();
          if (now - this.lastCleanupTime > this.cleanupInterval) {
            try {
              await this.cleanupOldLogs();
            } catch (error) {
              console.error('Auto-cleanup failed:', error);
            }
          }
          resolve(request.result as number);
        };

        request.onerror = () => reject(request.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Update a log entry by ID
   * @param id - Log entry ID
   * @param updatedData - Partial log fields to update
   * @returns Promise resolved when update is complete or rejected if not found
   */
  public async updateLog<T = any>(
    id: number,
    updatedData: Partial<LogEntry<T>>
  ): Promise<void> {
    const db = await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);

      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const existing = getRequest.result;

        if (!existing) {
          return reject(new Error(`Log entry with ID ${id} not found`));
        }

        const updatedLog: LogEntry<T> = {
          ...existing,
          ...this.sanitizeData(updatedData),
          id, // Ensure ID is preserved
        };

        try {
          const updateRequest = store.put(updatedLog);

          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } catch (error) {
          reject(error);
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Remove logs older than retention period
   * @returns Promise resolving to number of deleted logs
   */
  public async cleanupOldLogs(): Promise<number> {
    const db = await this.ready();
    const now = new Date();
    const cutoffDate = new Date(now);
    // Calculate oldest allowed date
    cutoffDate.setDate(now.getDate() - this.maxRetentionDays);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp_idx');
      // Create range for expired logs
      const range = IDBKeyRange.upperBound(cutoffDate);
      const request = index.openCursor(range);

      let deleteCount = 0;
      const deletePromises: Promise<void>[] = [];

      request.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          deleteCount++;
          // Queue delete operation
          deletePromises.push(
            new Promise<void>((resolveDelete, rejectDelete) => {
              const deleteRequest = cursor.delete();
              deleteRequest.onsuccess = () => resolveDelete();
              deleteRequest.onerror = () => rejectDelete(deleteRequest.error);
            })
          );
          cursor.continue();
        } else {
          // Resolve after all deletes complete
          Promise.all(deletePromises)
            .then(() => {
              this.lastCleanupTime = Date.now();
              resolve(deleteCount);
            })
            .catch(reject);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Retrieve logs with various filters
   * @param options - Query parameters
   * @returns Promise resolving to array of log entries
   */
  public async getLogs<T = any>(options?: GetOptions<T>) {
    // Apply retention policy to query dates
    const now = new Date();
    const minDate = new Date(now);
    minDate.setDate(now.getDate() - this.maxRetentionDays);

    const startDate = options?.startDate || new Date(new Date().setHours(0, 0, 0, 0));

    // Ensure query doesn't exceed retention period
    const effectiveStartDate = startDate
      ? new Date(Math.max(startDate.getTime(), minDate.getTime()))
      : minDate;

    return this._getLogs({
      ...options,
      startDate: effectiveStartDate,
    });
  }

  /**
   * Internal query implementation
   */
  private async _getLogs<T = any>(options?: GetOptions<T>) {
    const db = await this.ready();
    return new Promise<Array<LogEntry<T>>>((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp_idx');

      const startDate = options?.startDate || new Date(new Date().setHours(0, 0, 0, 0));
      const endDate =
        options?.endDate || new Date(new Date().setHours(23, 59, 59, 999));
      const batches = options?.batches || [this.batch];

      const range = IDBKeyRange.bound(startDate, endDate);

      // Open cursor in reverse chronological order
      const request = index.openCursor(range, 'next');
      const results: Array<LogEntry<T>> = [];

      request.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const log = cursor.value as LogEntry<T> & { date: string };

          log.date = `${log.timestamp.toLocaleString()}:${log.timestamp.getUTCMilliseconds()}`;

          const isKey = !options?.key || log.key.includes(options.key);
          const isKeyword =
            !options?.keyword || JSON.stringify(log).includes(options.keyword);
          const isFilter = !options?.filter || options.filter(log);
          const isBatch = !batches.length || batches.includes(log.batch);

          if (isKey && isKeyword && isFilter && isBatch) {
            results.push(log);
          }

          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all logs
   */
  public async clearLogs(): Promise<void> {
    const db = await this.ready();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update retention policy
   * @param days - New maximum retention days
   */
  public setMaxRetentionDays(days: number): void {
    this.maxRetentionDays = Math.max(1, days);
    // Trigger immediate cleanup with new policy
    this.cleanupOldLogs().catch(console.error);
  }

  /**
   * Export logs to a downloadable JSON file
   * @param options - Same options as getLogs (key, date range, filter)
   * @param fileName - Optional name for the exported file
   * @returns Promise resolved when the file is prepared and download started
   */
  public async exportLogsAsJson<T = any>(
    options?: GetOptions<T> & { excludeFields: string[] },
    fileName: string = 'exported-logs.json'
  ): Promise<void> {
    const logs = await this.getLogs<T>(options);

    // Optional: you could truncate or validate logs here before exporting

    const jsonContent = JSON.stringify(
      logs,
      (key, value) => {
        if (options?.excludeFields.includes(key)) {
          return undefined;
        }
        return value;
      },
      2
    ); // Pretty-print with 2 spaces

    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Create a temporary <a> element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    URL.revokeObjectURL(url);
  }
}

// Export singleton instance with default 3-day retention
export const logService = new LogService();

export function pushLog(key: string, info: any = null, write = true) {
  let result_: any;
  let time = Date.now();

  const isObject = info && typeof info === 'object';

  const args: [key: string, info?: any] = [key];

  if (isObject) {
    args[1] = Object.assign(
      {
        get ____result____() {
          return result_ === undefined ? this : result_;
        },
        get ____time____(): any {
          return result_ === undefined ? this : time;
        },
      },
      info
    );
  } else if (info !== null) {
    args[1] = info;
  }

  if (isDebug) {
    console.log(...args);
  }

  let p: Promise<any>;

  if (write) {
    if (isObject) {
      args[1] = Object.assign(
        {
          ____result____: undefined,
        },
        info
      );
    }

    p = logService.addLog(...args).catch((err) => {
      console.error(err, ...args);
    });
  }

  return (result: any) => {
    result_ = result;
    time = Date.now() - time;

    const data = {
      data: Object.assign(
        {
          ____result____: result_,
        },
        info
      ),
    };

    p?.then((id) => {
      logService.updateLog(id, data).catch((err) => {
        console.error(err, args[0], data, id);
      });
    });
  };
}

export async function exportLogs(options?: GetOptions) {
  const excludeFields = ['avatar', 'expect_read_list'];

  await logService.exportLogsAsJson({
    excludeFields,
    ...options,
  });

  message.success('Copied to clipboard');
}
